import React from "react";
import { Funnel, File } from "lucide-react";

import { Button } from "@/components/ui/button";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table } from "@tanstack/react-table";
import { Status } from "@/types/product";
import { Order } from "@/types/order";
import { TableView } from "..";
import { useHeader } from "./hooks";
import { OrderItemWithCurrency } from "../items-table/hooks";
import { formatPrice } from "@/utils/helpers";

interface Props {
  table: Table<Order> | Table<OrderItemWithCurrency>;
  setView: (view: TableView) => void;
  view: TableView;
}

const Header: React.FC<Props> = ({ table, setView, view }) => {
  const {
    currentStatusFilter,
    onOrdersClick,
    onItemsClick,
    onStatusFilterClick,
    totalSales,
    currency,
  } = useHeader({ table, view, setView });

  return (
    <div className="flex justify-between items-center gap-4 pb-4">
      <div className="flex items-center gap-6">
        <div className="flex flex-col justify-center border-b border-muted px-2">
          <span className="text-xs text-muted-foreground">Sales revenue</span>
          <span className="text-lg font-semibold">
            {formatPrice(totalSales, currency)}
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            variant={view === "orders" ? "default" : "outline"}
            className={`px-4 py-2 rounded transition-colors duration-150`}
            onClick={onOrdersClick}
          >
            Orders
          </Button>
          <Button
            variant={view === "items" ? "default" : "outline"}
            className={`px-4 py-2 rounded transition-colors duration-150`}
            onClick={onItemsClick}
          >
            Items
          </Button>
        </div>
      </div>
      <div className="flex gap-2">
        {view === "orders" ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                <Funnel /> Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {[
                { key: "All", label: "All" },
                { key: "pending", label: "Pending" },
                { key: "completed", label: "Completed" },
                { key: "draft", label: "Draft" },
                { key: "archived", label: "Archived" },
                { key: "canceled", label: "Canceled" },
                { key: "requires_action", label: "Requires Action" },
              ].map(({ key, label }) => {
                const isActive =
                  (key === "All" &&
                    (currentStatusFilter === undefined ||
                      currentStatusFilter === "")) ||
                  currentStatusFilter === key;

                const handleClick = () =>
                  onStatusFilterClick(
                    key === "All" ? undefined : (key as Status)
                  );

                return (
                  <DropdownMenuCheckboxItem
                    key={key}
                    checked={isActive}
                    onCheckedChange={handleClick}
                    className="capitalize"
                  >
                    {label}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        <Button variant="outline" className="ml-auto" disabled>
          <File /> Export
        </Button>
      </div>
    </div>
  );
};

export default Header;
